import { queryOptions } from '@tanstack/vue-query'
import { type MaybeRefOrGetter, toValue } from 'vue'
import { fetchYield } from '@/features/assets/api/fetchYield'
import { fetchAsset } from '@/features/assets/api/fetchAsset'
import { fetchYieldActivityLog } from '@/features/assets/api/fetchYieldActivityLog'
import { fetchPaginatedAssets } from '@/features/assets/api/fetchPaginatedAssets'
import { checkAssetCreationEligibility } from '@/features/assets/api/checkAssetCreationEligibility'
import { queryKeys } from '@/features/assets/query-keys'
import { AssetType } from '@/features/assets/constants'

type AssetPaginationOptions = Parameters<typeof queryKeys.assets.paginated>[0]

export const YieldQueries = {
  all: () => queryOptions({ queryKey: queryKeys.yields.all() }),

  fetch: (assetCode: MaybeRefOrGetter<string>) => queryOptions({
    queryKey: queryKeys.yields.details(assetCode),
    queryFn: () => fetchYield(toValue(assetCode)),
    meta: {
      logger: 'features/assets/queries/YieldQueries.fetch'
    }
  }),

  activityLog: (assetCode: MaybeRefOrGetter<string>) => queryOptions({
    queryKey: queryKeys.yields.activityLog(assetCode),
    queryFn: () => fetchYieldActivityLog(toValue(assetCode))
  })
} as const

export const assetQueries = {
  details: (assetCode: MaybeRefOrGetter<string|undefined>) => queryOptions({
    queryKey: queryKeys.assets.details(assetCode),
    enabled: () => toValue(assetCode) !== undefined,
    queryFn: () => fetchAsset(toValue(assetCode)!),
  }),

  paginated: (options: AssetPaginationOptions) => queryOptions({
    queryKey: queryKeys.assets.paginated(options),
    queryFn: () => fetchPaginatedAssets({
      page: toValue(options.page),
      pageSize: toValue(options.pageSize),
      filter: toValue(options.filter),
      sorting: toValue(options.sorting),
      owner: toValue(options.owner)
    })
  }),

  checkCreationEligibility: (data: { unifiiProjectCode: MaybeRefOrGetter<number|undefined>, assetType: MaybeRefOrGetter<AssetType>, wheelingProjectCode: MaybeRefOrGetter<string|undefined> }) => queryOptions({
    queryKey: queryKeys.assets.creationEligibilityLookup(data),
    enabled: () => typeof toValue(data.unifiiProjectCode) === 'number' && (toValue(data.assetType) !== AssetType.Wheeling || typeof toValue(data.wheelingProjectCode) === 'string'),
    queryFn: () => checkAssetCreationEligibility(toValue(data.unifiiProjectCode)!, toValue(data.assetType), toValue(data.wheelingProjectCode))
  })
} as const


// Backwards compatibility.
export const AssetQueries = { ...assetQueries, fetch: assetQueries.details, } as const
export const fetchPaginatedAssetsQueryOptions = assetQueries.paginated
export const checkAssetCreationEligibilityQueryOptions = assetQueries.checkCreationEligibility
